import type { AssetKind } from './models';

export const DRIVE_MIME = {
  folder: 'application/vnd.google-apps.folder',
  shortcut: 'application/vnd.google-apps.shortcut',
  document: 'application/vnd.google-apps.document',
  spreadsheet: 'application/vnd.google-apps.spreadsheet',
  presentation: 'application/vnd.google-apps.presentation',
} as const;

/** Archivos nativos de Google que se exportan a PDF para poder verlos y descargarlos. */
const EXPORTABLE_TO_PDF = new Set<string>([
  DRIVE_MIME.document,
  DRIVE_MIME.spreadsheet,
  DRIVE_MIME.presentation,
]);

/** Imágenes que cualquier navegador muestra; el resto (PSD, TIFF, HEIC…) se ofrece como descarga. */
const BROWSER_IMAGES = new Set([
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
  'image/svg+xml',
]);

const DOC_MIMES = new Set([
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'text/plain',
  'text/markdown',
  'text/x-markdown',
  'application/rtf',
]);

export const isGoogleNative = (mimeType: string): boolean =>
  mimeType.startsWith('application/vnd.google-apps.');

export const exportsToPdf = (mimeType: string): boolean => EXPORTABLE_TO_PDF.has(mimeType);

/** Si un archivo de Drive se puede copiar (binario) o exportar (Docs/Sheets/Slides). */
export function isCopyable(mimeType: string): boolean {
  if (!isGoogleNative(mimeType)) return true;
  return exportsToPdf(mimeType);
}

/** Extensiones de foto, video y audio que Drive no siempre etiqueta con un mime de medio. */
const IMAGE_EXT =
  /\.(jpe?g|png|gif|webp|avif|svg|heic|heif|tiff?|bmp|psd|psb|jxl|cr2|cr3|nef|nrw|arw|srf|sr2|dng|orf|raf|rw2|pef|srw|x3f|3fr|iiq|erf|kdc)$/i;
const VIDEO_EXT = /\.(mp4|m4v|mov|qt|webm|avi|mkv|wmv|mpe?g|mpg|3gp|3g2|mxf|mts|m2ts|flv|ogv)$/i;
const AUDIO_EXT = /\.(mp3|wav|wave|aac|m4a|flac|ogg|oga|opus|aiff?|aifc|wma|amr|caf|alac)$/i;

/** Lo único que se entrega al cliente: fotos, videos y audios. */
export const DELIVERABLE_KINDS: readonly AssetKind[] = ['image', 'video', 'audio'];
export const isDeliverableKind = (kind: AssetKind): boolean => DELIVERABLE_KINDS.includes(kind);

/** Audio que los navegadores reproducen directamente; el resto se ofrece para descargar. */
const BROWSER_AUDIO = /^audio\/(mpeg|mp3|wav|x-wav|wave|aac|mp4|x-m4a|ogg|opus|flac|x-flac|webm)$/;
export const isBrowserAudio = (mimeType: string): boolean => BROWSER_AUDIO.test(mimeType);

/** Video que los navegadores reproducen directamente (MOV depende del códec). */
const BROWSER_VIDEO = /^video\/(mp4|webm|ogg|quicktime|x-m4v)$/;
export const isBrowserVideo = (mimeType: string): boolean => BROWSER_VIDEO.test(mimeType);

/** Imagen que el navegador muestra tal cual (el resto se ve con su miniatura). */
export const isBrowserImage = (mimeType: string): boolean => BROWSER_IMAGES.has(mimeType);

export function kindOf(mimeType: string, name = ''): AssetKind {
  if (BROWSER_IMAGES.has(mimeType)) return 'image';
  if (mimeType.startsWith('video/') || VIDEO_EXT.test(name)) return 'video';
  if (mimeType.startsWith('audio/') || AUDIO_EXT.test(name)) return 'audio';
  if (mimeType.startsWith('image/') || IMAGE_EXT.test(name)) return 'image';
  if (mimeType === 'application/pdf') return 'pdf';
  if (exportsToPdf(mimeType) || DOC_MIMES.has(mimeType)) return 'doc';
  if (/\.(md|txt)$/i.test(name)) return 'doc';
  return 'other';
}

const stripAccents = (s: string): string => s.normalize('NFD').replace(/[̀-ͯ]/g, '');

/**
 * Convención de la agencia: los documentos de contexto viven en una subcarpeta cuyo nombre
 * contiene "contexto", "justificación" o "brief" (p. ej. "00. Contexto", "Brief cliente").
 * El prefijo numérico no cuenta: la agencia numera todas sus carpetas ("00. Propuesta").
 */
export function isContextFolderName(name: string): boolean {
  const n = stripAccents(name).toLowerCase().trim();
  return /contexto|justificacion|brief/.test(n);
}

/**
 * Forma de trabajo de la agencia: cada campaña es una carpeta con tres subcarpetas.
 *   Input   → lo que el cliente proporcionó (brief, lineamientos, materiales).
 *   Process → lo que la agencia fue haciendo (propuestas, racionales, pruebas).
 *   Output  → lo que se entrega al cliente.
 * Se aceptan variantes en español y prefijos de orden ("01. Input", "Proceso", "Entregables").
 */
export type WorkflowRole = 'input' | 'process' | 'output';

const WORKFLOW_NAMES: Record<WorkflowRole, RegExp> = {
  input: /^(inputs?|insumos?|entradas?)$/,
  process: /^(process|procesos?|wip)$/,
  output: /^(outputs?|entregables?|salidas?)$/,
};

export function workflowRole(folderName: string): WorkflowRole | null {
  const n = stripAccents(folderName)
    .toLowerCase()
    .replace(/^\s*\d{1,3}\s*[._\-)]?\s*/, '')
    .trim();
  for (const role of ['input', 'process', 'output'] as const) {
    if (WORKFLOW_NAMES[role].test(n)) return role;
  }
  return null;
}

/** Una carpeta es una campaña con la forma de trabajo si tiene su subcarpeta de Output. */
export const isWorkflowFolder = (childFolderNames: readonly string[]): boolean =>
  childFolderNames.some((name) => workflowRole(name) === 'output');

const SYSTEM_FILES = new Set(['.ds_store', 'thumbs.db', 'desktop.ini', 'icon\r', '.gitkeep']);

/** Archivos de sistema o metadatos (macOS, Windows) que nunca son entregables. */
export function isSystemFile(name: string): boolean {
  const n = name.trim().toLowerCase();
  return SYSTEM_FILES.has(n) || n.startsWith('._') || n.startsWith('~$');
}

export const isContextPath = (pathSegments: readonly string[]): boolean =>
  pathSegments.some(isContextFolderName);

/** Versión de contenido de un archivo: md5 para binarios, modifiedTime para archivos de Google. */
export function contentVersion(file: {
  md5Checksum?: string | null;
  modifiedTime: string;
  size?: number | null;
}): string {
  return file.md5Checksum ? `md5:${file.md5Checksum}` : `mt:${file.modifiedTime}`;
}

/** Nombre de campaña a partir de su carpeta: quita prefijos de orden ("01. Meta Ads" → "Meta Ads"). */
export function campaignNameFromFolder(folderName: string): string {
  const clean = folderName.replace(/^\s*\d{1,3}\s*[._\-)]?\s+/, '').trim();
  return clean || folderName.trim();
}
